import BaseEntity from '@/entity/BaseEntity';
import { pickFrom } from '@/utils/tools';
import { itemHas, useAside } from '@orion.ui/orion';
import UserProfileSpecificAvailabilitiesAsideNew from '@/components/user-profile/UserProfileSpecificAvailabilitiesAsideNew.vue';
import useAvailabilityService from '@/services/AvailabilityService';
import { PERIOD_DAY } from '@/typings/enums/PERIOD_DAY';


type Availability = Common<[IAvailabilityModel, ISpecificAvailabilityModel]> & {
  from?: string
  to?: string
}

export default class TempWorkerAvailabilityEntity extends BaseEntity<Availability> {

	get defaultAvailabilities () {
		return pickFrom(
			this,
			'id',
			'isAvailable',
			'availabilityDate',
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
			'sunday',
		);
	}

	get specificDefaultAvailabilities () {
		return pickFrom(
			this,
			'id',
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
			'sunday',
			'from',
			'to',
		);
	}

	get isAvailable () {
		if (itemHas(this.state, 'isAvailable'))
			return this.state.isAvailable;
		return undefined;
	}

	set isAvailable (val) {
		(this.state as IAvailabilityModel).isAvailable = val;
	}

	get availabilityDate () {
		if (itemHas(this.state, 'availabilityDate'))
			return this.state.availabilityDate;
		return undefined;
	}

	set availabilityDate (val) {
		if (itemHas(this.state, 'availabilityDate'))
			this.state.availabilityDate = val;
	}

	get from () { return this.state.from; }
	set from (val) { this.state.from = val; }

	get to () { return this.state.to; }
	set to (val) { this.state.to = val; }

	get monday () { return this.state.monday; }
	set monday (val) { this.state.monday = val; }

	get tuesday () { return this.state.tuesday; }
	set tuesday (val) { this.state.tuesday = val; }

	get wednesday () { return this.state.wednesday; }
	set wednesday (val) { this.state.wednesday = val; }

	get thursday () { return this.state.thursday; }
	set thursday (val) { this.state.thursday = val; }

	get friday () { return this.state.friday; }
	set friday (val) { this.state.friday = val; }

	get saturday () { return this.state.saturday; }
	set saturday (val) { this.state.saturday = val; }

	get sunday () { return this.state.sunday; }
	set sunday (val) { this.state.sunday = val; }

	get isNew () {
		return itemHas(this.state, 'isAvailable') || !this.state.id;
	}

	get dataForUpdateScheduleApi () {
		return {
			userId: this.self.id,
			...pickFrom(
				this,
				'id',
				'availabilityDate',
				'monday',
				'tuesday',
				'wednesday',
				'thursday',
				'friday',
				'saturday',
				'sunday',
			),
		} as IUpdateAvailabilityScheduleModel;
	}

	get dataForSpecificAvailabilityUpdateApi () {
		return pickFrom(
			this,
			'id',
			'from',
			'to',
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
			'sunday',
		) as IUpdateSpecificAvailabilityModel;
	}

	get dataForSpecificAvailabilityAddApi () {
		return pickFrom(
			this,
			'from',
			'to',
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
			'sunday',
		) as IAddSpecificAvailabilityModel;
	}

	async addSpecificAvailabilityAsync () {
		return await useAvailabilityService().addSpecificAsync(this.dataForSpecificAvailabilityAddApi);
	}

	async updateSpecificAvailabilityAsync () {
		return await useAvailabilityService().updateSpecificAsync(
			this.id,
			this.dataForSpecificAvailabilityUpdateApi,
		);
	}

	async deleteSpecificAvailabilityAsync () {
		await useAvailabilityService().deleteSpecificAsync(this.id);
	}

	async addFirstAsync () {
		const dataToSend = {
			isAvailable: true,
			availabilityDate: new Date().toPost(),
			monday: PERIOD_DAY.Morning | PERIOD_DAY.Afternoon,
			tuesday: PERIOD_DAY.Morning | PERIOD_DAY.Afternoon,
			wednesday: PERIOD_DAY.Morning | PERIOD_DAY.Afternoon,
			thursday: PERIOD_DAY.Morning | PERIOD_DAY.Afternoon,
			friday: PERIOD_DAY.Morning | PERIOD_DAY.Afternoon,
			saturday: PERIOD_DAY.None,
			sunday: PERIOD_DAY.None,
			userId: this.self.id,
		};

		const data = await useAvailabilityService().addAsync(dataToSend);
		this.assignData({
			...dataToSend,
			id: data,
		});

	}

	async updateGlobalAsync () {
		if (this.state.id) {
			await useAvailabilityService().updateIsAvailableAsync(this.state.id, this.isAvailable);
			if (this.availabilityDate && this.availabilityDate !== null && new Date() > new Date(this.availabilityDate)) {
				this.availabilityDate = undefined;
				await this.updateAsync();
			}
		} else {
			await this.addFirstAsync();
		}
	}

	async updateAsync () {
		await useAvailabilityService().updateScheduleAsync(this.dataForUpdateScheduleApi);
	}

	showSpecificAvailabilitiesAsideNew () {
		useAside({
			Nested: UserProfileSpecificAvailabilitiesAsideNew,
			NestedProps: { availabilityEntity: this },
		});
	}
}
